import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiTrendingDown,
  FiTrendingUp,
  FiUserCheck,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import PayrollStepper from "./components/PayrollStepper";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import PayrollPreviewAdvanceRecoverySetup from "./components/PayrollPreviewAdvanceRecoverySetup";

function indiaToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
  }).format(new Date());
}

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function number(value) {
  return Number(value || 0);
}

function formatMinutes(value) {
  const totalMinutes = Math.max(0, Math.round(number(value)));

  if (!totalMinutes) return "0 min";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes} min`;
  if (!minutes) return `${hours}h`;

  return `${hours}h ${minutes}m`;
}

function formatTime(value) {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));
}

function formatDate(value) {
  if (!value) return "—";

  const [year, month, day] = String(value).split("-").map(Number);

  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
}

function SessionTimeline({ sessions = [] }) {
  if (!sessions.length)
    return <span className="text-gray-400">No sessions</span>;
  return (
    <div className="flex min-w-[220px] flex-wrap gap-1.5">
      {sessions.map((session, index) => (
        <span
          key={session.id || index}
          className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
        >
          {formatTime(session.checkInAt)} - {formatTime(session.checkOutAt)}
        </span>
      ))}
    </div>
  );
}

function statusStyle(status) {
  const styles = {
    PRESENT: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    LATE: "bg-amber-50 text-amber-700 ring-amber-200",
    HALF_DAY: "bg-orange-50 text-orange-700 ring-orange-200",
    ABSENT: "bg-red-50 text-red-700 ring-red-200",
    ON_LEAVE: "bg-blue-50 text-blue-700 ring-blue-200",
    WEEKLY_OFF: "bg-violet-50 text-violet-700 ring-violet-200",
    HOLIDAY: "bg-violet-50 text-violet-700 ring-violet-200",
    ALREADY_SETTLED_OFF_CYCLE: "bg-gray-100 text-gray-600 ring-gray-200",
  };

  return styles[status] || "bg-gray-100 text-gray-600 ring-gray-200";
}

function statusLabel(status) {
  return String(status || "—")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function GenerateOffCyclePayroll() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [step, setStep] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [periodStart, setPeriodStart] = useState(indiaToday());
  const [periodEnd, setPeriodEnd] = useState(indiaToday());
  const [reason, setReason] = useState("");

  const [preview, setPreview] = useState(null);
  const [payrollRun, setPayrollRun] = useState(null);
  const [advanceRecoveries, setAdvanceRecoveries] = useState({});

  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDailyBreakdown, setShowDailyBreakdown] = useState(false);
  const [error, setError] = useState("");

  const loadEmployees = useCallback(async () => {
    if (!accessToken) return;

    try {
      setIsLoadingEmployees(true);

      const params = new URLSearchParams({
        status: "ACTIVE",
      });

      if (employeeSearch.trim()) {
        params.set("search", employeeSearch.trim());
      }

      const response = await apiRequest(`/employees?${params.toString()}`, {
        accessToken,
      });

      setEmployees(response?.items || response?.data || response || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load employees.");
      setEmployees([]);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [accessToken, employeeSearch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(loadEmployees, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadEmployees]);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === employeeId),
    [employees, employeeId],
  );

  const reviewPreview = async () => {
    if (!accessToken) return;

    if (!employeeId) {
      setError("Select the employee for this settlement.");
      return;
    }

    if (!periodStart || !periodEnd) {
      setError("Select both settlement dates.");
      return;
    }

    if (periodStart > periodEnd) {
      setError("Settlement start date cannot be after end date.");
      return;
    }

    if (!reason.trim()) {
      setError("Enter the reason for this off-cycle payroll.");
      return;
    }

    try {
      setIsLoadingPreview(true);
      setError("");
      setPreview(null);
      setPayrollRun(null);
      setAdvanceRecoveries({});
      setShowDailyBreakdown(false);

      const response = await apiRequest("/payroll/runs/off-cycle/preview", {
        method: "POST",
        accessToken,
        body: {
          employeeId,
          periodStart,
          periodEnd,
          reason: reason.trim(),
          advanceRecoveries: Object.entries(advanceRecoveries)
            .filter(
              ([, recovery]) => recovery.employeeAdvanceId && recovery.amount,
            )
            .map(([recoveryEmployeeId, recovery]) => ({
              employeeId: recoveryEmployeeId,
              employeeAdvanceId: recovery.employeeAdvanceId,
              amount: Number(recovery.amount),
            })),
        },
      });

      setPreview(response);
      setStep(2);
    } catch (requestError) {
      const message = Array.isArray(requestError.details?.message)
        ? requestError.details.message.join(", ")
        : requestError.message;

      setError(message || "Unable to calculate the payroll preview.");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const generateSettlement = async () => {
    if (!accessToken) return;

    try {
      setIsGenerating(true);
      setError("");

      const response = await apiRequest("/payroll/runs/off-cycle/generate", {
        method: "POST",
        accessToken,
        body: {
          employeeId,
          periodStart,
          periodEnd,
          reason: reason.trim(),
          advanceRecoveries: Object.entries(advanceRecoveries)
            .filter(
              ([, recovery]) =>
                recovery.employeeAdvanceId && Number(recovery.amount) > 0,
            )
            .map(([recoveryEmployeeId, recovery]) => ({
              employeeId: recoveryEmployeeId,
              employeeAdvanceId: recovery.employeeAdvanceId,
              amount: Number(recovery.amount),
            })),
        },
      });

      setPayrollRun(response);
      setStep(3);
    } catch (requestError) {
      const message = Array.isArray(requestError.details?.message)
        ? requestError.details.message.join(", ")
        : requestError.message;

      setError(message || "Unable to generate off-cycle payroll.");
    } finally {
      setIsGenerating(false);
    }
  };

  const previewSnapshot = preview?.calculationSnapshot || {};
  const dailyBreakdown = previewSnapshot.dailyBreakdown || [];
  const createdItem = payrollRun?.items?.[0];
  const selectedPreviewRecovery =
    advanceRecoveries[preview?.employee?.id] || {};
  const previewAdvanceRecoveryAmount =
    selectedPreviewRecovery.employeeAdvanceId && selectedPreviewRecovery.amount
      ? number(selectedPreviewRecovery.amount)
      : 0;
  const previewNetAfterAdvanceRecovery = Math.max(
    0,
    number(preview?.netPayable) - previewAdvanceRecoveryAmount,
  );
  const previewDeductionsAfterAdvanceRecovery =
    number(preview?.totalDeductions) + previewAdvanceRecoveryAmount;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">HRMS / Payroll</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Off-cycle Payroll
          </h1>

          <p className="mt-2 max-w-3xl text-gray-500">
            Calculate and settle an individual employee payout for final
            settlement, resignation, correction, or another exceptional period.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admindashboard/payroll")}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50"
        >
          <FiArrowLeft />
          Payroll Runs
        </button>
      </div>

      {error ? (
        <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" size={18} />
          <p>{error}</p>
        </div>
      ) : null}

      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
        <PayrollStepper
          currentStep={step}
          steps={["Employee", "Review", "Draft Created"]}
        />

        {step === 1 ? (
          <>
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900">
                Select employee and settlement period
              </h2>

              <p className="mt-1 text-gray-500">
                We will calculate attendance, leave, overtime, undertime,
                commissions, and deductions before a draft is created.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Search employee
                </label>

                <input
                  value={employeeSearch}
                  onChange={(event) => {
                    setEmployeeSearch(event.target.value);
                    setEmployeeId("");
                    setPreview(null);
                  }}
                  placeholder="Search by name or employee code"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />

                <label className="mb-2 mt-5 block text-sm font-medium text-gray-700">
                  Employee
                </label>

                <select
                  value={employeeId}
                  onChange={(event) => {
                    setEmployeeId(event.target.value);
                    setPreview(null);
                  }}
                  disabled={isLoadingEmployees}
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100 disabled:opacity-60"
                >
                  <option value="">
                    {isLoadingEmployees
                      ? "Loading employees..."
                      : "Select an active employee"}
                  </option>

                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName} · {employee.employeeCode}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-3xl border border-primary-100 bg-primary-50 p-6">
                <FiUserCheck className="text-primary-700" size={28} />

                <h3 className="mt-4 font-semibold text-gray-900">
                  Individual settlement
                </h3>

                <p className="mt-2 text-sm leading-6 text-gray-600">
                  This is for one employee only. Review every attendance day and
                  calculation before generating the draft.
                </p>

                {selectedEmployee ? (
                  <div className="mt-5 rounded-2xl border border-primary-100 bg-white p-4">
                    <p className="font-semibold text-gray-900">
                      {selectedEmployee.fullName}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {selectedEmployee.employeeCode}
                    </p>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Settlement start date
                </label>

                <input
                  type="date"
                  value={periodStart}
                  max={indiaToday()}
                  onChange={(event) => {
                    setPeriodStart(event.target.value);
                    setPreview(null);
                  }}
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Settlement end date
                </label>

                <input
                  type="date"
                  value={periodEnd}
                  min={periodStart}
                  max={indiaToday()}
                  onChange={(event) => {
                    setPeriodEnd(event.target.value);
                    setPreview(null);
                  }}
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Reason for settlement
                </label>

                <textarea
                  value={reason}
                  maxLength={500}
                  rows={4}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setPreview(null);
                  }}
                  placeholder="Example: Final settlement due to resignation on 06 August 2026."
                  className="w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />

                <p className="mt-2 text-right text-xs text-gray-400">
                  {reason.length}/500
                </p>
              </div>
            </div>

            <div className="mt-10 flex justify-end">
              <button
                type="button"
                onClick={reviewPreview}
                disabled={isLoadingPreview}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FiFileText />
                {isLoadingPreview ? "Calculating..." : "Calculate and Review"}
              </button>
            </div>
          </>
        ) : null}

        {step === 2 && preview ? (
          <>
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Payroll calculation review
                </h2>

                <p className="mt-1 max-w-3xl text-gray-500">
                  This preview is not saved yet. Confirm the attendance and
                  calculation below, then generate the payroll draft.
                </p>
              </div>

              <span className="inline-flex w-fit items-center gap-2 rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700">
                <FiClock />
                Preview only
              </span>
            </div>

            <section className="overflow-hidden rounded-3xl border border-gray-200">
              <div className="bg-gray-950 px-6 py-6 text-white sm:px-8">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                      Employee settlement
                    </p>

                    <h3 className="mt-2 text-2xl font-bold">
                      {preview.employee?.fullName}
                    </h3>

                    <p className="mt-1 text-sm text-white/70">
                      {preview.employee?.employeeCode} ·{" "}
                      {preview.employee?.salaryType === "DAILY"
                        ? "Daily salary"
                        : "Monthly salary"}
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/10 px-5 py-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-white/60">
                      Estimated net payout
                    </p>
                    <p className="mt-1 text-3xl font-bold">
                      {money(previewNetAfterAdvanceRecovery)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 divide-y divide-gray-100 bg-white md:grid-cols-3 md:divide-x md:divide-y-0">
                <ReviewMetric
                  label="Settlement period"
                  value={`${formatDate(preview.period?.start)} – ${formatDate(
                    preview.period?.end,
                  )}`}
                  icon={<FiCalendar />}
                />
                <ReviewMetric
                  label="Basic salary"
                  value={
                    preview.employee?.salaryType === "DAILY"
                      ? `${money(preview.employee?.basicSalary)} / day`
                      : `${money(preview.employee?.basicSalary)} / month`
                  }
                  icon={<FiDollarSign />}
                />
                <ReviewMetric
                  label="Daily payroll rate"
                  value={money(preview.dailySalaryRate)}
                  icon={<FiTrendingUp />}
                />
              </div>
            </section>

            <section className="mt-8">
              <SectionTitle
                title="Attendance and work summary"
                description="Attendance is calculated day-by-day from your attendance and approved leave records."
              />

              <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
                <StatCard
                  label="Payable days"
                  value={previewSnapshot.eligibleDays}
                />
                <StatCard
                  label="Present"
                  value={preview.presentDays}
                  tone="green"
                />
                <StatCard label="Late" value={preview.lateDays} tone="amber" />
                <StatCard
                  label="Half days"
                  value={preview.halfDays}
                  tone="orange"
                />
                <StatCard
                  label="Absent"
                  value={preview.absentDays}
                  tone="red"
                />
                <StatCard
                  label="Paid leave"
                  value={preview.paidLeaveDays}
                  tone="blue"
                />
                <StatCard
                  label="Unpaid leave"
                  value={preview.unpaidLeaveDays}
                  tone="red"
                />
                <StatCard
                  label="Holiday credits settled"
                  value={`${number(preview.holidayAllowanceUsed)} / ${number(
                    preview.monthlyHolidayAllowance,
                  )}`}
                  tone="purple"
                />
              </div>

              <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <InfoPanel
                  icon={<FiClock />}
                  title="Shift and worked time"
                  rows={[
                    [
                      "Shift",
                      previewSnapshot.shiftStartTime &&
                      previewSnapshot.shiftEndTime
                        ? `${previewSnapshot.shiftStartTime} – ${previewSnapshot.shiftEndTime}`
                        : "No shift configured",
                    ],
                    [
                      "Break duration",
                      formatMinutes(previewSnapshot.breakDurationMinutes),
                    ],

                    ["Overtime worked", formatMinutes(preview.overtimeMinutes)],
                    [
                      "Early exit time",
                      formatMinutes(preview.earlyExitMinutes),
                    ],
                    [
                      "Expected work/month",
                      formatMinutes(
                        number(previewSnapshot.expectedWorkingMinutes) *
                          number(previewSnapshot.eligibleDays),
                      ),
                    ],
                    [
                      "Total work/month",
                      formatMinutes(
                        (previewSnapshot.dailyBreakdown || []).reduce(
                          (total, day) => total + number(day.netWorkingMinutes),
                          0,
                        ),
                      ),
                    ],
                  ]}
                />

                <InfoPanel
                  icon={<FiTrendingUp />}
                  title="Earnings"
                  rows={[
                    ["Base salary", money(preview.baseSalaryAmount)],
                    [`Unused holiday payout (${number(previewSnapshot.holidayEncashmentDays)} days)`, money(previewSnapshot.holidayEncashmentAmount)],
                    ["Overtime", money(preview.overtimeAmount)],
                    ["Net commission", money(preview.netCommissionAmount)],
                    ["Gross earnings", money(preview.grossEarnings)],
                  ]}
                  highlight
                />

                <InfoPanel
                  icon={<FiTrendingDown />}
                  title="Deductions"
                  rows={[
                    [
                      `Unpaid absence (${number(
                        preview.unpaidAbsentDays,
                      )} days)`,
                      `-${money(preview.absenceDeductionAmount)}`,
                    ],
                    [
                      `Unpaid leave (${number(preview.unpaidLeaveDays)} days)`,
                      `-${money(preview.unpaidLeaveDeductionAmount)}`,
                    ],
                    [
                      `Early exit (${formatMinutes(preview.earlyExitMinutes)})`,
                      `-${money(preview.earlyExitDeductionAmount)}`,
                    ],
                    ["Total deductions", `-${money(preview.totalDeductions)}`],
                  ]}
                  danger
                />
              </div>
            </section>

            <section className="mt-8 overflow-hidden rounded-3xl border border-gray-200">
              <button
                type="button"
                onClick={() => setShowDailyBreakdown((value) => !value)}
                className="flex w-full items-center justify-between gap-5 bg-gray-50 px-6 py-5 text-left transition hover:bg-gray-100"
              >
                <div>
                  <h3 className="font-semibold text-gray-900">
                    Daily attendance and calculation details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    See the exact status, net worked minutes, overtime, and
                    early exit for every settlement date.
                  </p>
                </div>

                {showDailyBreakdown ? (
                  <FiChevronUp className="shrink-0 text-gray-600" size={20} />
                ) : (
                  <FiChevronDown className="shrink-0 text-gray-600" size={20} />
                )}
              </button>

              {showDailyBreakdown ? (
                <div className="overflow-x-auto">
                  <table className="min-w-[1000px] w-full text-left text-sm">
                    <thead className="border-y border-gray-200 bg-white text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-5 py-4 font-semibold">Date</th>
                        <th className="px-5 py-4 font-semibold">Status</th>
                        <th className="px-5 py-4 font-semibold">
                          Work sessions
                        </th>
                        <th className="px-5 py-4 font-semibold">
                          First / final
                        </th>
                        <th className="px-5 py-4 font-semibold">
                          Total worked
                        </th>
                        <th className="px-5 py-4 font-semibold">Overtime</th>
                        <th className="px-5 py-4 font-semibold">Under-time</th>
                        <th className="px-5 py-4 font-semibold">Day pay</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">
                      {dailyBreakdown.map((day) => (
                        <tr key={day.date} className="hover:bg-gray-50">
                          <td className="px-5 py-4 font-medium text-gray-900">
                            {formatDate(day.date)}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyle(
                                day.status,
                              )}`}
                            >
                              {statusLabel(day.status)}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <SessionTimeline sessions={day.sessions} />
                          </td>

                          <td className="px-5 py-4 text-gray-700">
                            <div>{formatTime(day.checkInAt)}</div>
                            <div className="mt-1 text-xs text-gray-400">
                              Final: {formatTime(day.checkOutAt)}
                            </div>
                          </td>

                          <td className="px-5 py-4 font-medium text-gray-900">
                            {day.netWorkingMinutes === null
                              ? "—"
                              : formatMinutes(day.netWorkingMinutes)}
                          </td>

                          <td className="px-5 py-4 text-emerald-700">
                            {formatMinutes(day.overtimeMinutes)}
                          </td>

                          <td className="px-5 py-4 text-red-700">
                            {formatMinutes(
                              day.underTimeMinutes ?? day.earlyExitMinutes,
                            )}
                          </td>

                          <td className="px-5 py-4 font-bold text-gray-900">
                            <div>{money(day.attendancePayableAmount)}</div>
                            {day.holidayEntitlementApplied ? (
                              <p className="mt-1 text-[11px] font-semibold text-primary-700">
                                Policy-paid absence
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      ))}

                      {!dailyBreakdown.length ? (
                        <tr>
                          <td
                            colSpan={8}
                            className="px-5 py-10 text-center text-gray-500"
                          >
                            No daily attendance details were found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>

            <section className="mt-8 rounded-3xl border border-primary-100 bg-primary-50 p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-primary-800">
                    Final payable amount
                  </p>
                  <p className="mt-1 text-3xl font-bold text-primary-900">
                    {money(previewNetAfterAdvanceRecovery)}
                  </p>
                  <p className="mt-2 text-sm text-primary-700">
                    Gross earnings {money(preview.grossEarnings)} minus
                    deductions {money(previewDeductionsAfterAdvanceRecovery)}.
                  </p>
                  {previewAdvanceRecoveryAmount ? (
                    <p className="mt-1 text-sm font-semibold text-primary-800">
                      Includes employee advance recovery of{" "}
                      {money(previewAdvanceRecoveryAmount)}.
                    </p>
                  ) : null}
                </div>

                <div className="max-w-sm rounded-2xl bg-white p-4 text-sm leading-6 text-gray-600">
                  <p className="font-semibold text-gray-900">
                    Reason for settlement
                  </p>
                  <p className="mt-1">{reason}</p>
                </div>
              </div>
            </section>

            <PayrollPreviewAdvanceRecoverySetup
              items={[preview]}
              accessToken={accessToken}
              recoveries={advanceRecoveries}
              onChange={setAdvanceRecoveries}
            />

            <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setPreview(null);
                  setAdvanceRecoveries({});
                }}
                disabled={isGenerating}
                className="rounded-2xl border border-gray-200 px-6 py-3 font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Edit Details
              </button>

              <button
                type="button"
                onClick={generateSettlement}
                disabled={isGenerating}
                className="rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGenerating
                  ? "Generating Draft..."
                  : "Confirm and Generate Draft"}
              </button>
            </div>
          </>
        ) : null}

        {step === 3 && payrollRun ? (
          <>
            <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-600 text-white">
                <FiCheckCircle size={28} />
              </div>

              <h2 className="mt-5 text-2xl font-bold text-gray-900">
                Draft settlement created
              </h2>

              <p className="mx-auto mt-2 max-w-xl text-gray-600">
                The payroll is still a draft. Review it in Payroll Runs, post
                it, and then mark the employee payout as paid.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              <ReviewMetric
                label="Draft net payable"
                value={money(createdItem?.netPayable)}
                icon={<FiDollarSign />}
              />
              <ReviewMetric
                label="Gross earnings"
                value={money(createdItem?.grossEarnings)}
                icon={<FiTrendingUp />}
              />
              <ReviewMetric
                label="Total deductions"
                value={money(createdItem?.totalDeductions)}
                icon={<FiTrendingDown />}
              />
            </div>

            <div className="mt-10 flex justify-end">
              <button
                type="button"
                onClick={() => navigate("/admindashboard/payroll")}
                className="rounded-2xl bg-primary-700 px-6 py-3 font-medium text-white transition hover:bg-primary-800"
              >
                Review Payroll Draft
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function SectionTitle({ title, description }) {
  return (
    <div className="mb-5">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
    </div>
  );
}

function StatCard({ label, value, tone = "gray" }) {
  const colors = {
    gray: "border-gray-100 bg-gray-50 text-gray-900",
    green: "border-emerald-100 bg-emerald-50 text-emerald-800",
    amber: "border-amber-100 bg-amber-50 text-amber-800",
    orange: "border-orange-100 bg-orange-50 text-orange-800",
    red: "border-red-100 bg-red-50 text-red-800",
    blue: "border-blue-100 bg-blue-50 text-blue-800",
    purple: "border-violet-100 bg-violet-50 text-violet-800",
  };

  return (
    <div className={`rounded-2xl border p-4 ${colors[tone]}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="mt-2 text-xl font-bold">{value ?? 0}</p>
    </div>
  );
}

function ReviewMetric({ label, value, icon }) {
  return (
    <div className="flex items-start gap-3 px-6 py-5">
      <div className="rounded-xl bg-primary-50 p-2.5 text-primary-700">
        {icon}
      </div>

      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="mt-1 font-semibold text-gray-900">{value || "—"}</p>
      </div>
    </div>
  );
}

function InfoPanel({ icon, title, rows, highlight = false, danger = false }) {
  const borderClass = danger
    ? "border-red-100"
    : highlight
      ? "border-emerald-100"
      : "border-gray-200";

  const iconClass = danger
    ? "bg-red-50 text-red-700"
    : highlight
      ? "bg-emerald-50 text-emerald-700"
      : "bg-primary-50 text-primary-700";

  return (
    <div className={`rounded-3xl border bg-white p-6 ${borderClass}`}>
      <div className="flex items-center gap-3">
        <div className={`rounded-xl p-2.5 ${iconClass}`}>{icon}</div>
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>

      <div className="mt-5 space-y-3">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-start justify-between gap-4 text-sm"
          >
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
